import React, { useState, useEffect, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import DatePicker, { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useTitle } from "../../../hooks/useTitle";
import { Alert } from "@material-ui/lab";

import {
	Paper,
	Grid,
	Card,
	CardContent,
	Typography,
	CircularProgress,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Box,
	InputAdornment,
	Avatar,
	Divider
} from "@material-ui/core";

import {
	GetApp as PdfIcon,
	DateRange as DateRangeIcon,
	AttachMoney as MoneyIcon,
	Receipt as ReceiptIcon,
	TrendingDown as RefundIcon,
	MonetizationOn as AverageIcon
} from "@material-ui/icons";

import {
	PieChart,
	Pie,
	Cell,
	Tooltip,
	Legend,
	ResponsiveContainer,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	LabelList
} from "recharts";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { AuthContext } from "../../../context/Auth/AuthContext";

registerLocale("es", es);

const COLORS_INCOME = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];
const COLORS_REFUND = ["#FF8042", "#FF6B6B", "#FF5252", "#D32F2F", "#B71C1C"];
const COLORS_BARS = ["#3f51b5", "#2196f3", "#03a9f4", "#00bcd4", "#009688"];

const methodTranslations = {
	cash: "Efectivo",
	card: "Tarjeta",
	transfer: "Transf.",
	check: "Cheque",
	other: "Otro"
};

const useStyles = makeStyles((theme) => ({
	mainPaper: {
		padding: theme.spacing(2),
	},
	reportContainer: {
		padding: theme.spacing(2),
		borderRadius: theme.shape.borderRadius,
		backgroundColor: theme.palette.type === "dark" 
			? theme.palette.background.default 
			: "#f5f5f5",
		border: theme.palette.type === "dark" ? "1px solid " + theme.palette.divider : "none",
	},
	statCard: {
		height: "100%",
		position: "relative",
		overflow: "hidden",
		backgroundColor: theme.palette.background.paper,
		transition: "transform 0.2s, box-shadow 0.2s",
		"&:hover": {
			transform: "translateY(-2px)",
			boxShadow: theme.shadows[4]
		}
	},
	cardContentBox: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: theme.spacing(2)
	},
	cardTextBox: {
		flex: 1,
		minWidth: 0,
	},
	chartPaper: {
		padding: theme.spacing(2),
		height: "100%",
		minHeight: 340, 
		backgroundColor: theme.palette.background.paper,
		borderRadius: theme.shape.borderRadius,
		boxShadow: theme.shadows[1],
	},
	filterContainer: {
		marginBottom: theme.spacing(3),
	},
	cardTitle: {
		fontWeight: "bold",
		textTransform: "uppercase",
		fontSize: "0.75rem",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis"
	},
	cardValue: {
		fontWeight: 700,
		marginTop: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			fontSize: "1.5rem"
		}
	},
	cardAvatar: {
		width: 48,
		height: 48,
		flexShrink: 0,
		boxShadow: "0 4px 8px rgba(0,0,0,0.15)"
	},
	centeredBox: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		height: 250,
	},
	alertBox: {
		marginBottom: theme.spacing(3),
	},
	alert: {
		backgroundColor: theme.palette.background.paper,
	},
	sectionSpacer: {
		marginBottom: theme.spacing(3),
	},
	divider: {
		margin: theme.spacing(3, 0),
	}
}));

const StatCard = ({ title, value, icon, color, subValue }) => {
	const classes = useStyles();
	return (
		<Card className={classes.statCard} elevation={2}>
			<CardContent>
				<div className={classes.cardContentBox}>
					<div className={classes.cardTextBox}>
						<Typography color="textSecondary" className={classes.cardTitle}>
							{title}
						</Typography>
						<Typography variant="h4" className={classes.cardValue} style={{ color: color }}>
							{value}
						</Typography>
						{subValue && (
							<Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
								{subValue}
							</Typography>
						)}
					</div>
					<Avatar className={classes.cardAvatar} style={{ backgroundColor: color }}>
						{icon}
					</Avatar>
				</div>
			</CardContent>
		</Card>
	);
};

const FinancialReports = () => {
	useTitle("Reportes Financieros");
	const classes = useStyles();
	const theme = useTheme();
	const { user: currentUser } = useContext(AuthContext);
	const [loading, setLoading] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [reportData, setReportData] = useState(null);
	
	const toLocalDateParam = (date) => {
		if (!date) return null;
		const y = date.getFullYear();
		const m = String(date.getMonth() + 1).padStart(2, "0");
		const d = String(date.getDate()).padStart(2, "0");
		return `${y}-${m}-${d}`;
	};
	
	const [startDate, setStartDate] = useState(() => {
		const date = new Date();
		return new Date(date.getFullYear(), date.getMonth(), 1);
	});
	const [endDate, setEndDate] = useState(() => {
		const date = new Date();
		return new Date(date.getFullYear(), date.getMonth() + 1, 0);
	});

	const [doctors, setDoctors] = useState([]);
	const [selectedDoctorId, setSelectedDoctorId] = useState("");

	useEffect(() => {
		const fetchDoctors = async () => {
			try {
				const { data } = await api.get("/users", { params: { profile: "doctor" } });
				setDoctors(data.users || []);
			} catch (err) {
				toastError(err);
			}
		};
		if (currentUser.profile !== "doctor") {
			fetchDoctors();
		}
	}, [currentUser]);

	const fetchReport = useCallback(async () => {
		if (!startDate || !endDate) return;
		setLoading(true);
		try {
			const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

			const params = {
				startDate: toLocalDateParam(startDate),
				endDate: toLocalDateParam(endDate),
				timeZone: timeZone
			};

			if (currentUser.profile === "doctor") {
				params.doctorId = currentUser.id;
			} else if (selectedDoctorId) {
				params.doctorId = selectedDoctorId;
			}

			const { data } = await api.get("/reports/summary", { params });
			setReportData(data);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	}, [startDate, endDate, selectedDoctorId, currentUser]);

	useEffect(() => {
		fetchReport();
	}, [fetchReport]);

	const handleDownloadPDF = async () => {
		const input = document.getElementById("report-content");
		if (!input) return;

		setExporting(true);

		try {
			const clone = input.cloneNode(true);
			
			clone.style.width = "1100px"; 
			clone.style.padding = "0px";
			clone.style.margin = "0px";
			clone.style.overflow = "visible";
			
			const container = document.createElement("div");
			container.style.position = "absolute";
			container.style.left = "-9999px";
			container.style.top = "0";
			container.style.width = "1100px"; 
			container.appendChild(clone);
			document.body.appendChild(container);

			await new Promise(resolve => setTimeout(resolve, 500));

			const canvas = await html2canvas(clone, { 
				scale: 2, 
				useCORS: true,
				width: 1100, 
				windowWidth: 1100,
				x: 0,
				y: 0,
				scrollX: 0,
				scrollY: 0,
				backgroundColor: "#ffffff" 
			});
			
			const imgData = canvas.toDataURL("image/png");
			
			document.body.removeChild(container);

			let titleText = "Reporte Financiero";
			if (currentUser.profile === "doctor") {
				titleText = "Reporte Financiero de " + currentUser.name;
			} else if (selectedDoctorId) {
				const doc = doctors.find(d => d.id === selectedDoctorId);
				if (doc) titleText = "Reporte Financiero de " + doc.name;
			}

			const pdfWidth = 595.28; 
			const margin = 20;
			const headerHeight = 80;
			
			const contentWidth = pdfWidth - (margin * 2);
			const imgProps = { width: canvas.width, height: canvas.height };
			const pdfImgHeight = (imgProps.height * contentWidth) / imgProps.width;

			const totalHeight = headerHeight + pdfImgHeight + margin;

			const pdf = new jsPDF("p", "pt", [pdfWidth, totalHeight]);

			pdf.setFontSize(18);
			pdf.text(titleText, margin, 40);
			
			pdf.setFontSize(11);
			pdf.setTextColor(100);
			const dateStr = startDate.toLocaleDateString() + " al " + endDate.toLocaleDateString();
			pdf.text("Período: " + dateStr, margin, 60);
			
			pdf.addImage(imgData, "PNG", margin, headerHeight, contentWidth, pdfImgHeight);
			
			const startStr = startDate.toLocaleDateString("es-ES").replace(/\//g, "-");
			const endStr = endDate.toLocaleDateString("es-ES").replace(/\//g, "-");
			const fileName = "Reporte_Financiero_" + startStr + "_al_" + endStr + ".pdf";
			
			pdf.save(fileName);
			toast.success("PDF generado con éxito");

		} catch (err) {
			console.error(err);
			toast.error("Error al generar PDF");
		} finally {
			setExporting(false);
		}
	};

	const formatData = (items) => {
		if (!items) return [];
		return items
			.filter(i => Number(i.value) > 0)
			.map(i => ({
				name: methodTranslations[i.name] || i.name,
				value: Number(i.value)
			}));
	};

	const dataMethods = formatData(reportData?.groupByPaymentMethod?.payments);
	const dataRefunds = formatData(reportData?.groupByPaymentMethod?.refunds);
	
	const totalGrossIncome = dataMethods.reduce((acc, curr) => acc + curr.value, 0);
	const totalRefundMoney = dataRefunds.reduce((acc, curr) => acc + curr.value, 0);

	const dataDoctors = (reportData?.groupByDoctor?.payments || [])
		.filter(d => Number(d.value) > 0)
		.map(d => ({ name: d.name, value: Number(d.value) }))
		.sort((a, b) => b.value - a.value)
		.slice(0, 5);

	const dataTitles = (reportData?.groupByTitle?.payments || [])
		.filter(t => Number(t.value) > 0)
		.map(t => ({ name: t.name, value: Number(t.value) }))
		.sort((a, b) => b.value - a.value);

	return (
		<Paper variant="outlined" className={classes.mainPaper}>
			<Grid container spacing={2} alignItems="center" className={classes.filterContainer}>
				{currentUser.profile !== "doctor" && (
					<Grid item xs={12} md={3}>
						<FormControl variant="outlined" size="small" fullWidth>
							<InputLabel>Doctor</InputLabel>
							<Select
								value={selectedDoctorId}
								onChange={(e) => setSelectedDoctorId(e.target.value)}
								label="Doctor"
								MenuProps={{
									getContentAnchorEl: null,
									anchorOrigin: { vertical: "bottom", horizontal: "left" },
									transformOrigin: { vertical: "top", horizontal: "left" }
								}}
							>
								<MenuItem value="">Todos los Doctores</MenuItem>
								{doctors.map(d => (
									<MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				)}
				<Grid item xs={6} md={3}>
					<DatePicker
						selected={startDate}
						onChange={(date) => setStartDate(date)}
						selectsStart
						startDate={startDate}
						endDate={endDate}
						locale="es"
						dateFormat="dd/MM/yyyy"
						customInput={
							<TextField 
								label="Desde" 
								variant="outlined" 
								size="small" 
								fullWidth 
								InputProps={{ startAdornment: <InputAdornment position="start"><DateRangeIcon /></InputAdornment> }}
							/>
						}
					/>
				</Grid>
				<Grid item xs={6} md={3}>
					<DatePicker
						selected={endDate}
						onChange={(date) => setEndDate(date)}
						selectsEnd
						startDate={startDate}
						endDate={endDate}
						minDate={startDate}
						locale="es"
						dateFormat="dd/MM/yyyy"
						customInput={
							<TextField 
								label="Hasta" 
								variant="outlined" 
								size="small" 
								fullWidth 
								InputProps={{ startAdornment: <InputAdornment position="start"><DateRangeIcon /></InputAdornment> }}
							/>
						}
					/>
				</Grid>
				<Grid item xs={12} md={currentUser.profile !== "doctor" ? 3 : 6}>
					<Button
						variant="contained"
						color="primary"
						fullWidth
						onClick={handleDownloadPDF}
						disabled={loading || exporting || !reportData}
						startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <PdfIcon />}
					>
						Descargar Reporte
					</Button>
				</Grid>
			</Grid>

			{loading ? (
				<Box display="flex" justifyContent="center" p={5}>
					<CircularProgress />
				</Box>
			) : !reportData ? (
				<Box p={4} textAlign="center">
					<Typography color="textSecondary">No hay datos para el rango seleccionado.</Typography>
				</Box>
			) : (
				<>
					<div className={classes.alertBox}>
						<Alert severity="info" variant="outlined" className={classes.alert}>
							<strong>Nota Importante:</strong> Este reporte se basa en la <strong>fecha real de la transacción (pago o devolución)</strong>, no en la fecha de la cita médica.
							Si una cita de enero se paga en febrero, aparecerá en el reporte de febrero.
						</Alert>
					</div>

					<div id="report-content" className={classes.reportContainer}>
						<Grid container spacing={2} className={classes.sectionSpacer}>
							<Grid item xs={12} sm={6} md={3}>
								<StatCard 
									title="Ingresos Brutos" 
									value={"$" + totalGrossIncome.toFixed(2)} 
									subValue={reportData.kpis.chargedAppointments + " citas cobradas"}
									icon={<MoneyIcon style={{ color: "white" }} />} 
									color="#2196f3" 
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<StatCard 
									title="Total Reembolsado" 
									value={"$" + totalRefundMoney.toFixed(2)} 
									subValue={reportData.kpis.totalRefunds + " devoluciones"}
									icon={<RefundIcon style={{ color: "white" }} />} 
									color="#f44336" 
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<StatCard 
									title="Ingresos Netos" 
									value={"$" + reportData.kpis.totalRevenue.toFixed(2)} 
									subValue="Real en caja"
									icon={<ReceiptIcon style={{ color: "white" }} />} 
									color="#4caf50" 
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<StatCard 
									title="Ticket Promedio" 
									value={"$" + reportData.kpis.averageTicket.toFixed(2)} 
									icon={<AverageIcon style={{ color: "white" }} />} 
									color="#9c27b0" 
								/>
							</Grid>
						</Grid>

						<Divider className={classes.divider} />

						<Grid container spacing={2}>
							
							<Grid item xs={12} md={6}>
								<Paper className={classes.chartPaper} elevation={1}>
									<Typography variant="h6" align="center" gutterBottom>Ingresos por Medio de Pago</Typography>
									<ResponsiveContainer width="100%" height={300}>
										<PieChart>
											<Pie
												data={dataMethods}
												dataKey="value"
												nameKey="name"
												cx="50%"
												cy="50%"
												outerRadius={80}
												label={({ name, value, percent }) => name + ": $" + value + " (" + (percent * 100).toFixed(0) + "%)"}
											>
												{dataMethods.map((entry, index) => (
													<Cell key={"cell-method-" + index} fill={COLORS_INCOME[index % COLORS_INCOME.length]} />
												))}
											</Pie>
											<Tooltip formatter={(value) => "$" + value.toFixed(2)} />
											<Legend />
										</PieChart>
									</ResponsiveContainer>
								</Paper>
							</Grid>

							<Grid item xs={12} md={6}>
								<Paper className={classes.chartPaper} elevation={1}>
									<Typography variant="h6" align="center" gutterBottom>Devoluciones / Reintegros</Typography>
									{dataRefunds.length > 0 ? (
										<ResponsiveContainer width="100%" height={300}>
											<PieChart>
												<Pie
													data={dataRefunds}
													dataKey="value"
													nameKey="name"
													cx="50%"
													cy="50%"
													innerRadius={50}
													outerRadius={80}
													label={({ name, value }) => name + ": $" + value}
												>
													{dataRefunds.map((entry, index) => (
														<Cell key={"cell-refund-" + index} fill={COLORS_REFUND[index % COLORS_REFUND.length]} />
													))}
												</Pie>
												<Tooltip formatter={(value) => "$" + value.toFixed(2)} />
												<Legend />
											</PieChart>
										</ResponsiveContainer>
									) : (
										<Box className={classes.centeredBox}>
											<Typography color="textSecondary">No hay devoluciones en este período.</Typography>
										</Box>
									)}
								</Paper>
							</Grid>

							<Grid item xs={12} md={6}>
								<Paper className={classes.chartPaper} elevation={1}>
									<Typography variant="h6" align="center" gutterBottom>Top 5 Ingresos por Doctor</Typography>
									<ResponsiveContainer width="100%" height={300}>
										<BarChart data={dataDoctors} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
											<CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
											<XAxis type="number" hide />
											<YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12, fill: theme.palette.text.primary }} />
											<Tooltip 
												formatter={(value) => "$" + value.toFixed(2)} 
												cursor={{fill: "transparent"}}
											/>
											<Bar dataKey="value" name="Ingresos" fill="#3f51b5" radius={[0, 4, 4, 0]}>
												<LabelList 
													dataKey="value" 
													position="right" 
													formatter={(val) => "$" + val}
													style={{ fontSize: "11px", fill: theme.palette.text.primary, fontWeight: "bold" }} 
												/>
												{dataDoctors.map((entry, index) => (
													<Cell key={"cell-doc-" + index} fill={COLORS_BARS[index % COLORS_BARS.length]} />
												))}
											</Bar>
										</BarChart>
									</ResponsiveContainer>
								</Paper>
							</Grid>

							<Grid item xs={12} md={6}>
								<Paper className={classes.chartPaper} elevation={1}>
									<Typography variant="h6" align="center" gutterBottom>Ingresos por Especialidad</Typography>
									<ResponsiveContainer width="100%" height={300}>
										<BarChart data={dataTitles} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
											<CartesianGrid strokeDasharray="3 3" vertical={false} />
											<XAxis dataKey="name" tick={{ fontSize: 12, fill: theme.palette.text.primary }} />
											<YAxis />
											<Tooltip formatter={(value) => "$" + value.toFixed(2)} />
											<Bar dataKey="value" name="Ingresos" fill="#009688">
												<LabelList 
													dataKey="value" 
													position="top" 
													formatter={(val) => "$" + val}
													style={{ fontSize: "11px", fill: theme.palette.text.primary, fontWeight: "bold" }}
												/>
												{dataTitles.map((entry, index) => (
													<Cell key={"cell-title-" + index} fill={COLORS_BARS[(index + 2) % COLORS_BARS.length]} />
												))}
											</Bar>
										</BarChart>
									</ResponsiveContainer>
								</Paper>
							</Grid>

						</Grid>
					</div>
				</>
			)}
		</Paper>
	);
};

export default FinancialReports;